import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetBuyerList,
  GetBuyerDetail,
  GetBuyerListForArea,
  GetBuyerListForReceipt,
  GetBuyerListForFieldSelect,
  CreateBuyer,
  UpdateBuyer,
  DeleteBuyer,
} from "../../Services/BuyerService";

/**
 * Buyer State Interface with caching and optimization
 */
export interface BuyerState {
  // List data
  buyerCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;

  // Detail data
  selectedBuyerDetail: any | null;

  // Area-specific buyers
  buyersByArea: any[];

  // Receipt mode buyers
  receiptBuyers: any[];

  // Field select mode buyers
  fieldSelectBuyers: any[];

  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;

  // Error states
  error: string | null;
  errorDetail: string | null;

  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  mode: string;

  // Cache management
  cache: Map<string, CacheEntry>;
  requestAbortController: AbortController | null;
  lastFetchParams: FetchParams | null;

  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number; // milliseconds
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  mode: string;
  financialYear: string;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Generate cache key based on fetch parameters
 */
const generateCacheKey = (params: FetchParams): string => {
  return `buyer_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.mode}`;
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

/**
 * Async Thunk: Fetch buyer list with pagination and search
 */
export const fetchBuyerList = createAsyncThunk(
  "buyer/fetchBuyerList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      mode: string;
      financialYear: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { buyer: BuyerState };
      const cacheKey = generateCacheKey(params);

      // Check if we have valid cache
      if (root.buyer.cache.has(cacheKey)) {
        const cached = root.buyer.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("mode", params.mode);
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetBuyerList(urlParams);

      if (result?.data?.data?.buyerCollection) {
        return result.data.data;
      }

      return rejectWithValue("No buyer data received");
    } catch (error: any) {
      console.error("Error fetching buyer list:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch buyers"
      );
    }
  }
);

/**
 * Async Thunk: Fetch single buyer detail
 */
export const fetchBuyerDetail = createAsyncThunk(
  "buyer/fetchBuyerDetail",
  async (
    params: {
      consumerID: number;
      firmId: number;
      branchId: number;
      buyerID: number;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerID", params.consumerID.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("buyerID", params.buyerID.toString());
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetBuyerDetail(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No buyer detail received");
    } catch (error: any) {
      console.error("Error fetching buyer detail:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch buyer details"
      );
    }
  }
);

/**
 * Async Thunk: Fetch buyers for area
 */
export const fetchBuyersByArea = createAsyncThunk(
  "buyer/fetchBuyersByArea",
  async (params: any, { rejectWithValue }) => {
    try {
      const urlParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        urlParams.append(key, String(value));
      });

      const result: any = await GetBuyerListForArea(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No buyer data received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch buyers for area"
      );
    }
  }
);

/**
 * Async Thunk: Fetch buyers for receipt
 */
export const fetchBuyersForReceipt = createAsyncThunk(
  "buyer/fetchBuyersForReceipt",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      searchValue: string;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetBuyerListForReceipt(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No buyer data received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch receipt buyers"
      );
    }
  }
);

/**
 * Async Thunk: Fetch buyers for field select
 */
export const fetchBuyersForFieldSelect = createAsyncThunk(
  "buyer/fetchBuyersForFieldSelect",
  async (params: any, { rejectWithValue }) => {
    try {
      const urlParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        urlParams.append(key, String(value));
      });

      const result: any = await GetBuyerListForFieldSelect(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No buyer data received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch field select buyers"
      );
    }
  }
);

/**
 * Async Thunk: Create new buyer
 */
export const createBuyerAsync = createAsyncThunk(
  "buyer/createBuyer",
  async (buyerData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateBuyer(buyerData);

      if (result?.data) {
        return result.data;
      }

      return rejectWithValue("Failed to create buyer");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create buyer"
      );
    }
  }
);

/**
 * Async Thunk: Update buyer
 */
export const updateBuyerAsync = createAsyncThunk(
  "buyer/updateBuyer",
  async (buyerData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateBuyer(buyerData);

      if (result?.data) {
        return result.data;
      }

      return rejectWithValue("Failed to update buyer");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update buyer"
      );
    }
  }
);

/**
 * Async Thunk: Delete buyer
 */
export const deleteBuyerAsync = createAsyncThunk(
  "buyer/deleteBuyer",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteBuyer(deleteData);
      // Check if the data property exists and is not null
      if (result?.data || result?.Data) {
        return deleteData; // Return the ID that was deleted
      }

      return rejectWithValue("Failed to delete buyer");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete buyer"
      );
    }
  }
);

const initialState: BuyerState = {
  buyerCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedBuyerDetail: null,
  buyersByArea: [],
  receiptBuyers: [],
  fieldSelectBuyers: [],
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  mode: "",
  cache: new Map(),
  requestAbortController: null,
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const buyerSlice = createSlice({
  name: "buyer",
  initialState,
  reducers: {
    /**
     * Clear all buyer data
     */
    clearBuyerData: (state) => {
      state.buyerCollection = [];
      state.totalCount = 0;
      state.selectedBuyerDetail = null;
      state.error = null;
    },

    /**
     * Clear buyer detail
     */
    clearBuyerDetail: (state) => {
      state.selectedBuyerDetail = null;
      state.errorDetail = null;
    },

    /**
     * Clear errors
     */
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },

    /**
     * Set buyer collection manually
     */
    setBuyerCollection: (state, action: PayloadAction<any[]>) => {
      state.buyerCollection = action.payload;
    },

    /**
     * Clear success flags
     */
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },

    /**
     * Invalidate cache for refetch
     */
    invalidateCache: (state) => {
      state.cache.clear();
    },

    /**
     * Set fetch metadata
     */
    setFetchMetadata: (
      state,
      action: PayloadAction<Partial<FetchParams>>
    ) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
      state.mode = action.payload.mode || state.mode;
    },
  },
  extraReducers: (builder) => {
    /**
     * Fetch Buyer List handlers
     */
    builder
      .addCase(fetchBuyerList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(
        fetchBuyerList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingList = false;
          state.buyerCollection = action.payload?.buyerCollection || [];
          state.totalCount = action.payload?.totalCount || 0;
          state.pageIndex = action.payload?.pageIndex || 1;
          state.pageSize = action.payload?.pageSize || 10;

          // Update cache
          if (state.lastFetchParams) {
            const cacheKey = generateCacheKey(state.lastFetchParams);
            state.cache.set(cacheKey, {
              data: action.payload,
              timestamp: Date.now(),
              expiresIn: CACHE_DURATION,
            });
          }
        }
      )
      .addCase(fetchBuyerList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    /**
     * Fetch Buyer Detail handlers
     */
    builder
      .addCase(fetchBuyerDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(
        fetchBuyerDetail.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingDetail = false;
          state.selectedBuyerDetail = action.payload;
        }
      )
      .addCase(fetchBuyerDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    /**
     * Fetch Buyers By Area handlers
     */
    builder
      .addCase(fetchBuyersByArea.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(
        fetchBuyersByArea.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingList = false;
          state.buyersByArea = action.payload?.buyerCollection || [];
        }
      )
      .addCase(fetchBuyersByArea.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    /**
     * Fetch Buyers For Receipt handlers
     */
    builder
      .addCase(fetchBuyersForReceipt.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(
        fetchBuyersForReceipt.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingList = false;
          state.receiptBuyers = action.payload?.buyerCollection || [];
        }
      )
      .addCase(fetchBuyersForReceipt.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    /**
     * Fetch Buyers For Field Select handlers
     */
    builder
      .addCase(fetchBuyersForFieldSelect.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(
        fetchBuyersForFieldSelect.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingList = false;
          state.fieldSelectBuyers = action.payload?.buyerCollection || [];
        }
      )
      .addCase(fetchBuyersForFieldSelect.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    /**
     * Create Buyer handlers
     */
    builder
      .addCase(createBuyerAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createBuyerAsync.fulfilled, (state, action) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear(); // Invalidate cache on create
      })
      .addCase(createBuyerAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    /**
     * Update Buyer handlers
     */
    builder
      .addCase(updateBuyerAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateBuyerAsync.fulfilled, (state, action) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear(); // Invalidate cache on update
      })
      .addCase(updateBuyerAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    /**
     * Delete Buyer handlers
     */
    builder
      .addCase(deleteBuyerAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteBuyerAsync.fulfilled, (state, action) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear(); // Invalidate cache on delete
      })
      .addCase(deleteBuyerAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearBuyerData,
  clearBuyerDetail,
  clearErrors,
  setBuyerCollection,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = buyerSlice.actions;

export default buyerSlice.reducer;
